/* eslint-disable max-len */
import React, { FC, useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { uniqueId } from 'lodash'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'
import {
  fetchTaskGanttChartList,
  fetchTaskGanttChart,
  updateTaskLabelGroupCategory,
  updateTaskLabelSettingLabelGroup,
  fetchTaskGanttChartDetail,
  updateTaskLabel,
  updateTaskLabelMember,
} from '../../../../states/containers/task/taskSlice'
import Holidays from 'date-holidays'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import GanttDetailModal from '../../../components/task/GanttDetailModal'
import GanttChart from '../../../components/task/GanttChart'
import { PackageBlock, RequestGetTaskGanttChartDetail, ResponseGetTaskGanttChart, ResponseGetTaskGanttChartDetail, ResponseGetTaskGanttChartList } from '../../../../states/types/task'
import GanttSettingModal from '../../../components/task/GanttSettingModal'
import { dateManipulateDay, format } from '../../../../utils/date'
import GanttUserSettingModal from '../../../components/task/GanttUserSettingModal'
import { updateTaskGanttChartDisplayTypeInit, updateTaskGanttChartRangeTypeInit } from '../../../../states/containers/common/currentSlice'
import GanttUserAssignModal from '../../../components/task/GanttUserAssignModal'
import GanttSearchModal from '../../../components/task/GanttSearchModal'

interface ModalData {
  isOpen: boolean
  id: number
  blockType: string
  displayType: string,
}

interface Member {
  member_id: number
  name: string
  kana: string
  code: string | null
  profile_image: string | null
}

interface ConfigData {
  id: number
  name: string
  color: string
  schedule: {
    start_date: string | null
    end_date: string | null
    time: number
  }
  member_list: Member[]
  parent_id?: number
}

interface DetailData {
  memberList: Member[]
  labelGroupId?: number
  bgColor?: string
  editable?: boolean
}

const Index: FC = () => {
  const dispatch = useDispatch()

  const {
    task,
    current,
    login,
  } = useSelector((state: RootState) => state)

  const holidays = new Holidays()
  holidays.init('JP')

  const modalDataInit: ModalData = {
    isOpen: false,
    id: 0,
    blockType: "",
    displayType: "",
  }

  const [ganttChartList, setGanttChartList] = useState<ResponseGetTaskGanttChartList>()
  const [ganttCharts, setGanttCharts] = useState<ResponseGetTaskGanttChart[]>([])
  const [ganttChartDetail, setGanttChartDetail] = useState<ResponseGetTaskGanttChartDetail>()
  const [configData, setConfigData] = useState<ConfigData>()
  const [detailData, setDetailData] = useState<DetailData>()

  const [configModalData, setConfigModalData] = useState<ModalData>(modalDataInit)
  const [detailModalData, setDetailModalData] = useState<ModalData>(modalDataInit)

  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false)

  const [isDisplayCategory, setIsDisplayCategory] = useState<boolean>(true)

  const [isOpenUserSetting, setIsOpenUserSetting] = useState<boolean>(false)

  const [assignedMemberIds, setAssignedMemberIds] = useState<number[]>([])

  const [userAssignModal, setUserAssignModal] = useState<{memberId: number, name: string, labelId: number, labelGroupId: number}>({ memberId: 0, name: "", labelId: 0, labelGroupId: 0 })
  const [isOpenUserAssign, setIsOpenUserAssign] = useState<boolean>(false)

  const [searchDate, setSearchDate] = useState<string | null>(null)
  const [searchKeyword, setSearchKeyword] = useState<string>("")
  const [isOpenSearch, setIsOpenSearch] = useState<boolean>(false)

  const findBlock = (values: (ResponseGetTaskGanttChart | PackageBlock)[], blockType: string, id: number): ResponseGetTaskGanttChart | PackageBlock | null => {
    if (!values.length) return null
    const value = values.find((v) => v.block_type === blockType && v.id === id)
    if (value) return value
    return values.map((v) => findBlock(v.children || [], blockType, id)).find((v) => v) || null
  }

  const openModal = (params: {blockType: string, id: number, modalType: string, displayType: string, labelGroupId?: number, parentId?: number, assignedMemberIds?: number[], bgColor?: string}) => {
    if (params.modalType === "detail") {
      setDetailModalData({
        isOpen: true,
        id: params.id,
        blockType: params.blockType,
        displayType: params.displayType,
      })
      let param:RequestGetTaskGanttChartDetail = {}
      if (params.blockType === "label_group_category") {
        param = { label_group_category_id: params.id }
        setAssignedMemberIds([])
      } else if (params.blockType === "label_group") {
        param = { label_group_id: params.id }
        setAssignedMemberIds([])
      } else if (params.blockType === "label") {
        param = { label_id: params.id }
        setAssignedMemberIds(params.assignedMemberIds || [])
        setDetailData({
          memberList: ganttChartList?.label_groups.find((v) => v.id === params.labelGroupId)?.member_list || [],
          labelGroupId: params.labelGroupId,
          bgColor: params.bgColor,
          editable: ganttChartList?.label_groups.find((v) => v.id === params.labelGroupId)?.editable || false,
        })
      }
      setIsLoadingDetail(true)
      dispatch(
        fetchTaskGanttChartDetail(
          param,
          () => {
            setIsLoadingDetail(false)
          },
        ),
      )
      return
    }
    if (params.modalType === "config") {
      const tmpMemberList = ganttChartList?.label_groups.find((v) => v.id === params.labelGroupId)?.member_list || []
      if (params.blockType === "member" && params.parentId) {
        const parent = findBlock(ganttCharts, "label", params.parentId)
        const member = ganttChartDetail?.members.find((v) => v.member_id === params.id)
        if (!parent || !member || !parent.assigned_members.find((v) => v.member_id === params.id)) {
          setUserAssignModal({
            memberId: member?.member_id || 0,
            name: member?.name || "",
            labelId: parent?.id || 0,
            labelGroupId: params.labelGroupId || 0,
          })
          setIsOpenUserAssign(true)
          return
        }
        setConfigData({
          id: member.member_id,
          name: member.name,
          color: parent.color,
          schedule: member.schedule,
          member_list: tmpMemberList,
          parent_id: parent.id,
        })
      } else {
        const tmp = findBlock(ganttCharts, params.blockType, params.id)
        if (!tmp) return
        const detailParams: ConfigData = {
          id: tmp.id,
          name: tmp.name,
          color: tmp.color,
          schedule: tmp.schedule,
          member_list: tmpMemberList,
        }
        if (params.blockType === "label" && params.labelGroupId) {
          setAssignedMemberIds(params.assignedMemberIds || [])
          detailParams.parent_id = params.labelGroupId
        }
        setConfigData(detailParams)
      }
      setConfigModalData({
        isOpen: true,
        id: params.id,
        blockType: params.blockType,
        displayType: params.displayType,
      })
    }
  }

  const closeDetailModal = () => {
    setDetailModalData({
      ...modalDataInit,
      isOpen: false,
    })
  }

  const closeConfigModal = () => {
    setConfigModalData({
      ...modalDataInit,
      isOpen: false,
    })
  }

  const fetchGanttChart = (blockType: string, id: string, callback: VoidFunction) => {
    dispatch(
      fetchTaskGanttChart(
        {
          id: id,
          block_type: blockType,
        },
        callback,
      ),
    )
  }

  const buildGanttChartsLabelList = (values: (ResponseGetTaskGanttChart | PackageBlock)[], data?: {blockType: string, id: number, parentId?: number, memberIds?: number[], startDate: string | null, endDate: string | null, time?: number, status?: string | null, callback: VoidFunction}) => {
    if (!values) return []
    return values.map((v):(string | null)[] => {
      let tmp: (string | null)[] = []
      if (v.block_type === "label") {
        if (data?.blockType === "member" && v.id === data.parentId) {
          tmp = v.assigned_members.map((w) => (w.member_id === data.id ? data.status || null : w.status))
        } else {
          tmp = v.assigned_members.map((w) => w.status)
        }
      }
      return [...tmp, ...buildGanttChartsLabelList(v.children, data)]
    }).flat()
  }

  const rebuildGanttCharts = (values: (ResponseGetTaskGanttChart | PackageBlock)[], data?: {blockType: string, id: number, parentId?: number, memberIds?: number[], startDate: string | null, endDate: string | null, time?: number, status?: string | null, callback: VoidFunction}) => {
    if (!values) return []
    return values.map((v):ResponseGetTaskGanttChart | PackageBlock => {
      const statusList = buildGanttChartsLabelList([v], data)
      let status:string | null | undefined = null
      if (!statusList?.length) {
        status = null
      } else if (statusList?.includes("pending")) {
        status = "pending"
      } else if (statusList?.includes("in_progress")) {
        status = "in_progress"
      } else if (statusList?.every((x) => x === "completed")) {
        status = "completed"
      } else if (statusList?.includes("completed")) {
        status = "in_completed"
      }
      if (v.block_type === data?.blockType && v.id === data?.id) {
        const scheduleParam:{ start_date: string | null, end_date: string | null, time?: number } = { start_date: data.startDate, end_date: data.endDate }
        if (data.time !== undefined) scheduleParam.time = data.time || 0
        if ("min_date" in v && data.startDate && data.endDate) {
          const minDate = Number(dateManipulateDay(v.min_date, -10, "YYYYMMDD")) < Number(dateManipulateDay(data.startDate, -10, "YYYYMMDD")) ? v.min_date : data.startDate
          const maxDate = Number(dateManipulateDay(v.max_date, 10, "YYYYMMDD")) > Number(dateManipulateDay(data.endDate, 10, "YYYYMMDD")) ? v.max_date : data.endDate
          return {
            ...v,
            min_date: minDate,
            max_date: maxDate,
            schedule: { ...v.schedule, ...scheduleParam },
            status: status !== undefined ? status : v.status,
            assigned_members: ganttChartList?.label_groups.find((w) => w.id === data.parentId)?.member_list.filter((w) => data.memberIds?.includes(w.member_id)).map((w) => ({ ...w, status: v.assigned_members.find((x) => x.member_id === w.member_id)?.status || null })) || [],
            children: rebuildGanttCharts(v.children, data),
          }
        }
        return {
          ...v,
          schedule: { ...v.schedule, ...scheduleParam },
          status: status !== undefined ? status : v.status,
          assigned_members: ganttChartList?.label_groups.find((w) => w.id === data.parentId)?.member_list.filter((w) => data.memberIds?.includes(w.member_id)).map((w) => ({ ...w, status: v.assigned_members.find((x) => x.member_id === w.member_id)?.status || null })) || [],
          children: rebuildGanttCharts(v.children, data),
        }
      }
      if ("min_date" in v && data?.startDate && data?.endDate) {
        const minDate = Number(dateManipulateDay(v.min_date, -10, "YYYYMMDD")) < Number(dateManipulateDay(data.startDate, -10, "YYYYMMDD")) ? v.min_date : data.startDate
        const maxDate = Number(dateManipulateDay(v.max_date, 10, "YYYYMMDD")) > Number(dateManipulateDay(data.endDate, 10, "YYYYMMDD")) ? v.max_date : data.endDate
        return {
          ...v,
          min_date: minDate,
          max_date: maxDate,
          status: status !== undefined ? status : v.status,
          children: rebuildGanttCharts(v.children, data),
        }
      }
      let tmpAssignedMembers = v.assigned_members
      if (v.block_type === "label" && data?.blockType === "member" && v.id === data.parentId) {
        tmpAssignedMembers = v.assigned_members.map((w) => (w.member_id === data.id ? { ...w, status: data.status || null } : w))
      }
      return {
        ...v,
        status: status !== undefined ? status : v.status,
        assigned_members: tmpAssignedMembers,
        children: rebuildGanttCharts(v.children, data),
      }
    })
  }

  const updateGanttChartDetail = (data: {blockType: string, id: number, parentId?: number, memberIds?: number[], startDate: string | null, endDate: string | null, time?: number, status?: string | null, callback: VoidFunction}) => {
    const params: {id: string, label_id?: string, scheduled_start_date?: string | null, scheduled_end_date?: string | null, scheduled_time?: number, member_ids?: number[], status?: string | null} = {
      id: data.id.toString(),
    }
    if (data.startDate) {
      params.scheduled_start_date = data.startDate
    }
    if (data.endDate) {
      params.scheduled_end_date = data.endDate
    }
    if (data.time !== undefined) {
      params.scheduled_time = data.time
    }
    if (data.status !== undefined) {
      params.status = data.status || null
    }
    const scheduleParam:{ start_date: string | null, end_date: string | null, time?: number } = { start_date: data.startDate, end_date: data.endDate }
    if (data.time !== undefined) scheduleParam.time = data.time || 0
    if (data.blockType === "label_group_category") {
      dispatch(
        updateTaskLabelGroupCategory(params, data.callback),
      )
    } else if (data.blockType === "label_group") {
      dispatch(
        updateTaskLabelSettingLabelGroup(params, data.callback),
      )
    } else if (data.blockType === "label") {
      if (data.memberIds !== undefined) {
        params.member_ids = data.memberIds
      }
      const tmpMemberList = ganttChartList?.label_groups.find((v) => v.id === data.parentId)?.member_list || []
      dispatch(
        updateTaskLabel(
          params,
          () => {
            data.callback()
            setAssignedMemberIds(data.memberIds || [])
            if (ganttChartDetail && ganttChartDetail.id === data.id) {
              const currentMemberIds = ganttChartDetail.members.map((v) => v.member_id)
              const addMemberIds = data.memberIds?.filter((v) => !currentMemberIds.includes(v))
              const tmpMembers = [
                ...ganttChartDetail.members.filter((v) => data.memberIds?.includes(v.member_id) || v.actual.length).map((v) => {
                  if (data.memberIds?.includes(v.member_id)) return v
                  return { ...v, schedule: { start_date: null, end_date: null, time: 0 } }
                }),
                ...tmpMemberList.filter((v) => addMemberIds?.includes(v.member_id)).map((v) => ({
                  ...v,
                  schedule: {
                    start_date: null,
                    end_date: null,
                    time: 0,
                  },
                  actual: [],
                  status: null,
                })),
              ]
              const minDate = Number(dateManipulateDay(ganttChartDetail.min_date, -10, "YYYYMMDD")) < Number(dateManipulateDay(data.startDate, -10, "YYYYMMDD")) ? ganttChartDetail.min_date : data.startDate
              const maxDate = Number(dateManipulateDay(ganttChartDetail.max_date, 10, "YYYYMMDD")) > Number(dateManipulateDay(data.endDate, 10, "YYYYMMDD")) ? ganttChartDetail.max_date : data.endDate
              setGanttChartDetail({
                ...ganttChartDetail,
                min_date: minDate || ganttChartDetail.min_date,
                max_date: maxDate || ganttChartDetail.max_date,
                schedule: { ...ganttChartDetail.schedule, ...scheduleParam },
                members: tmpMembers,
              })
            }
          },
        ),
      )
    } else if (data.blockType === "member") {
      dispatch(
        updateTaskLabelMember(
          {
            ...params,
            label_id: (data.parentId || 0).toString(),
          },
          () => {
            if (ganttChartDetail) {
              const minDate = Number(dateManipulateDay(ganttChartDetail.min_date, -10, "YYYYMMDD")) < Number(dateManipulateDay(data.startDate, -10, "YYYYMMDD")) ? ganttChartDetail.min_date : data.startDate
              const maxDate = Number(dateManipulateDay(ganttChartDetail.max_date, 10, "YYYYMMDD")) > Number(dateManipulateDay(data.endDate, 10, "YYYYMMDD")) ? ganttChartDetail.max_date : data.endDate
              setGanttChartDetail({
                ...ganttChartDetail,
                min_date: minDate || ganttChartDetail.min_date,
                max_date: maxDate || ganttChartDetail.max_date,
                members: ganttChartDetail.members.map((v) => (
                  v.member_id === data.id
                    ? { ...v, schedule: { start_date: data.startDate, end_date: data.endDate, time: data.time !== undefined ? data.time : v.schedule.time }, status: data.status !== undefined ? data.status || null : v.status }
                    : v
                )),
              })
            }
            data.callback()
          },
        ),
      )
    }
    if (!ganttChartList) return
    const tmpLabelGroupCategory = ganttChartList.label_group_categories.map((v) => {
      if (data.blockType === "label_group_category" && data.id === v.id) {
        return { ...v, schedule: { ...v.schedule, ...scheduleParam } }
      }
      return { ...v }
    })
    const tmpLabelGroup = ganttChartList.label_groups.map((v) => {
      if (data.blockType === "label_group" && data.id === v.id) {
        return { ...v, schedule: { ...v.schedule, ...scheduleParam } }
      }
      return { ...v }
    })
    setGanttChartList({ label_group_categories: tmpLabelGroupCategory, label_groups: tmpLabelGroup })
    setGanttCharts(rebuildGanttCharts(ganttCharts, data) as ResponseGetTaskGanttChart[])
  }

  const updateGanttChartDetailMember = (callback: VoidFunction) => {
    updateGanttChartDetail({
      blockType: "label",
      id: userAssignModal.labelId,
      parentId: userAssignModal.labelGroupId,
      memberIds: [...assignedMemberIds, userAssignModal.memberId],
      startDate: ganttChartDetail?.schedule.start_date || null,
      endDate: ganttChartDetail?.schedule.end_date || null,
      time: ganttChartDetail?.schedule.time,
      callback: callback,
    })
  }

  const sortGanttChartList = (values: (ResponseGetTaskGanttChart | PackageBlock)[], parent: {blockType: string, id: number}, children: {blockType: string, id: number}[]) => {
    if (!values) return []
    return values.map((v):ResponseGetTaskGanttChart | PackageBlock => {
      if (v.block_type === parent.blockType && v.id === parent.id) {
        const sortChildren:PackageBlock[] = []
        children.forEach((w) => {
          const tmp = v.children.find((x) => x.id === w.id)
          if (tmp) sortChildren.push(tmp)
        })
        return { ...v, children: sortGanttChartList(sortChildren, parent, children) }
      }
      return { ...v, children: sortGanttChartList(v.children, parent, children) }
    })
  }

  const updateGanttChartDetailSort = (parent: {blockType: string, id: number}, children: {blockType: string, id: number, label_master_id?: number}[], callback: VoidFunction) => {
    if (parent.blockType === "label_group_category") {
      const params = {
        id: parent.id.toString(),
        sequences: children.map((v) => ({ block_type: v.blockType, id: v.id })),
      }
      dispatch(
        updateTaskLabelGroupCategory(params, callback),
      )
    } else if (parent.blockType === "label_group") {
      const params = {
        id: parent.id.toString(),
        labels: children.map((v) => ({ label_id: v.id, label_master_id: v.label_master_id })),
      }
      dispatch(
        updateTaskLabelSettingLabelGroup(params, callback),
      )
    }
    setGanttCharts(sortGanttChartList(ganttCharts, parent, children) as ResponseGetTaskGanttChart[])
  }

  const updateGanttUserSetting = (displayType: string, displayRange: string, callback: VoidFunction) => {
    dispatch(updateTaskGanttChartDisplayTypeInit(displayType))
    dispatch(updateTaskGanttChartRangeTypeInit(displayRange))
    callback()
  }

  const updateSearch = (date: string, keyword: string) => {
    setSearchDate(date || null)
    setSearchKeyword(keyword)
    setIsOpenSearch(false)
  }

  const resetSearch = () => {
    setSearchDate(null)
    setSearchKeyword("")
  }

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
  }, [])

  useEffect(() => {
    dispatch(fetchTaskGanttChartList())
    setIsDisplayCategory(current.taskGanttChartDisplayTypeInit === "category")
  }, [])

  useEffect(() => {
    if (!task.taskGanttChartList) return
    setGanttChartList(task.taskGanttChartList)
  }, [task.taskGanttChartList])

  useEffect(() => {
    if (!task.taskGanttCharts) return
    const tmpData = task.taskGanttCharts.map((v) => {
      const target = ganttCharts.find((w) => w.block_type === v.block_type && w.id === v.id)
      return target || v
    })
    setGanttCharts(rebuildGanttCharts(tmpData) as ResponseGetTaskGanttChart[])
  }, [task.taskGanttCharts])

  useEffect(() => {
    if (!task.taskGanttChartDetail) return
    setGanttChartDetail(task.taskGanttChartDetail)
  }, [task.taskGanttChartDetail])

  useEffect(() => {
    if (!task.taskGanttChartList?.label_groups.length) return
    const dateNum = searchDate ? Number(format(searchDate, "YYYYMMDD")) : null
    let tmpLabelGroupCategory = task.taskGanttChartList.label_group_categories
    let tmpLabelGroup = task.taskGanttChartList.label_groups
    if (dateNum) {
      tmpLabelGroupCategory = tmpLabelGroupCategory.filter((v) => (
        Number(format(v.schedule.start_date, "YYYYMMDD")) <= dateNum
        && Number(format(v.schedule.end_date, "YYYYMMDD")) >= dateNum
      ))
      tmpLabelGroup = tmpLabelGroup.filter((v) => (
        Number(format(v.schedule.start_date, "YYYYMMDD")) <= dateNum
        && Number(format(v.schedule.end_date, "YYYYMMDD")) >= dateNum
      ))
    }
    if (searchKeyword) {
      tmpLabelGroupCategory = tmpLabelGroupCategory.filter((v) => (
        v.name.indexOf(searchKeyword) > -1
      ))
      tmpLabelGroup = tmpLabelGroup.filter((v) => (
        v.name.indexOf(searchKeyword) > -1
      ))
    }
    setGanttChartList({ label_group_categories: tmpLabelGroupCategory, label_groups: tmpLabelGroup })
  }, [searchDate, searchKeyword])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - ガントビュー(βテスト)</title>
      </Helmet>
      <section className="breadcrumb">
        ガントビュー(βテスト)
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          ガントビュー(βテスト)
        </h1>
      </section>
      <section className="page_description">
        <p>
          プロジェクトの予定と実績をガントチャート形式で表示・編集します。
        </p>
        <p>
          ※ βテスト版公開のため、操作方法や仕様は予告なく変更される可能性があります。正式版リリースの際には改めて機能をご案内いたします。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title" key={uniqueId()}>
              {`${isDisplayCategory ? "カテゴリー" : "プロジェクト"}別ガントビュー`}
              <div className="button_area">
                {
                  isDisplayCategory ? (
                    <>
                      <Tips
                        description="プロジェクト表示に切り替えます"
                        className=""
                        icon={false}
                      >
                        <>
                          <button
                            type="button"
                            onClick={() => setIsDisplayCategory(false)}
                            style={{
                              border: "1px solid #cccccc",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              display: "flex",
                              gap: "4px",
                              fontSize: "12px",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'folder']} />
                            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                            <FontAwesomeIcon icon={['fas', 'list-ul']} />
                          </button>
                        </>
                      </Tips>
                    </>
                  ) : (
                    <>
                      <Tips
                        description="カテゴリー表示に切り替えます"
                        className=""
                        icon={false}
                      >
                        <>
                          <button
                            type="button"
                            onClick={() => setIsDisplayCategory(true)}
                            style={{
                              border: "1px solid #cccccc",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              display: "flex",
                              gap: "4px",
                              fontSize: "12px",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'list-ul']} />
                            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                            <FontAwesomeIcon icon={['fas', 'folder']} />
                          </button>
                        </>
                      </Tips>
                    </>
                  )
                }
              </div>
            </div>
            <Tips
              description="ガントビューのユーザー設定"
              className=""
              icon={false}
            >
              <>
                <button
                  type="button"
                  style={{
                    border: "solid 1px #CCCCCC",
                    borderRadius: "4px",
                    padding: "4px",
                    color: "#666666",
                    backgroundColor: "#FFFFFF",
                  }}
                  onClick={() => setIsOpenUserSetting(true)}
                >
                  <FontAwesomeIcon
                    icon={['fas', 'user-cog']}
                    className=""
                    fixedWidth
                  />
                </button>
              </>
            </Tips>
          </div>
          <div
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              marginBottom: "16px",
              backgroundColor: "#FFFFFF",
              padding: "8px",
              display: "flex",
              flexFlow: "column",
              gap: "8px",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                color: "#666666",
              }}
            >
              絞り込み検索
            </div>
            <div
              style={{
                display: "flex",
                gap: "16px",
                padding: "4px 8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    borderRadius: "4px",
                    padding: "2px 4px",
                    fontSize: "10px",
                    backgroundColor: "#666666",
                    color: "#FFFFFF",
                  }}
                >
                  予定
                </div>
                <div>
                  {
                    searchDate ? (
                      <>
                        {`${format(searchDate, "YYYY年M月D日")}を含む`}
                      </>
                    ) : (
                      <>
                        指定なし
                      </>
                    )
                  }
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    borderRadius: "4px",
                    padding: "2px 4px",
                    fontSize: "10px",
                    backgroundColor: "#666666",
                    color: "#FFFFFF",
                  }}
                >
                  キーワード
                </div>
                <div>
                  {
                    searchKeyword ? (
                      <>
                        {searchKeyword}
                      </>
                    ) : (
                      <>
                        指定なし
                      </>
                    )
                  }
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {
                !!(searchDate || searchKeyword) && (
                  <button
                    type="button"
                    style={{
                      borderRadius: "4px",
                      padding: "4px 6px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "12px",
                      backgroundColor: "#FF0000",
                      color: "#FFFFFF",
                    }}
                    onClick={resetSearch}
                  >
                    絞り込み条件リセット
                  </button>
                )
              }
              <button
                type="button"
                style={{
                  border: "1px solid #CCCCCC",
                  borderRadius: "4px",
                  padding: "4px 6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "12px",
                  backgroundColor: "#FFFFFF",
                }}
                onClick={() => setIsOpenSearch(true)}
              >
                絞り込み条件変更
              </button>
            </div>
          </div>
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    情報を取得しています…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  {
                    isDisplayCategory ? (
                      <>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "16px",
                          }}
                        >
                          {
                            ganttChartList?.label_group_categories.map((v) => (
                              <GanttChart
                                key={`gantt_label_group_categories_${v.name}_${v.id}_${v.schedule.time}`}
                                labelData={v}
                                detailDataList={ganttCharts?.find((w) => w.id === v.id && w.block_type === "label_group_category")}
                                fetchCallback={(blockType, id, callback) => fetchGanttChart(blockType, id, callback)}
                                updateCallback={(blockType, id, startDate, endDate, callback, memberIds, parentId) => updateGanttChartDetail({
                                  blockType: blockType,
                                  id: id,
                                  startDate: startDate,
                                  endDate: endDate,
                                  memberIds: memberIds,
                                  parentId: parentId,
                                  callback: callback,
                                })}
                                sortCallback={(parent, children, callback) => updateGanttChartDetailSort(parent, children, callback)}
                                openModalCallback={(params) => openModal(params)}
                                displayBlockType="label_group_category"
                              />
                            ))
                          }
                          {
                            !ganttChartList?.label_group_categories.length && (
                              <>
                                <div
                                  style={{
                                    textAlign: "center",
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: "#999999",
                                  }}
                                >
                                  表示するデータがありません
                                </div>
                              </>
                            )
                          }
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "16px",
                          }}
                        >
                          {
                            ganttChartList?.label_groups.map((v) => (
                              <GanttChart
                                key={`gantt_label_groups_${v.name}_${v.id}`}
                                labelData={v}
                                detailDataList={ganttCharts?.find((w) => w.id === v.id && w.block_type === "label_group")}
                                fetchCallback={(blockType, id, callback) => fetchGanttChart(blockType, id, callback)}
                                updateCallback={(blockType, id, startDate, endDate, callback, memberIds, parentId) => updateGanttChartDetail({
                                  blockType: blockType,
                                  id: id,
                                  startDate: startDate,
                                  endDate: endDate,
                                  memberIds: memberIds,
                                  parentId: parentId,
                                  callback: callback,
                                })}
                                sortCallback={(parent, children, callback) => updateGanttChartDetailSort(parent, children, callback)}
                                openModalCallback={(params) => openModal(params)}
                                displayBlockType="label_group"
                              />
                            ))
                          }
                          {
                            !ganttChartList?.label_groups.length && (
                              <>
                                <div
                                  style={{
                                    textAlign: "center",
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: "#999999",
                                  }}
                                >
                                  表示するデータがありません
                                </div>
                              </>
                            )
                          }
                        </div>
                      </>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
      <GanttDetailModal
        closeCallback={closeDetailModal}
        updateCallback={(params) => updateGanttChartDetail(params)}
        openModalCallback={(params) => openModal(params)}
        isDisplay={detailModalData.isOpen}
        displayBlockType={detailModalData.blockType}
        dataList={ganttChartDetail}
        isLoading={isLoadingDetail}
        displayType={detailModalData.displayType}
        assignedMemberIds={assignedMemberIds}
        detailData={detailData}
        currentMemberId={login.current_member_id || 0}
      />
      <GanttSettingModal
        closeCallback={closeConfigModal}
        updateCallback={(params) => updateGanttChartDetail(params)}
        isDisplay={configModalData.isOpen}
        blockType={configModalData.blockType}
        configData={configData}
        assignedMemberIds={assignedMemberIds}
      />
      <GanttUserSettingModal
        closeCallback={() => setIsOpenUserSetting(false)}
        updateCallback={(displayType, displayRange, callback) => updateGanttUserSetting(displayType, displayRange, callback)}
        isDisplay={isOpenUserSetting}
      />
      <GanttUserAssignModal
        closeCallback={() => setIsOpenUserAssign(false)}
        updateCallback={(callback) => updateGanttChartDetailMember(callback)}
        memberData={userAssignModal}
        isDisplay={isOpenUserAssign}
      />
      <GanttSearchModal
        closeCallback={() => setIsOpenSearch(false)}
        updateCallback={(date, keyword) => updateSearch(date, keyword)}
        searchData={{ date: searchDate || "", keyword: searchKeyword }}
        isDisplay={isOpenSearch}
      />
    </section>
  )
}

export default Index
